/**
 * Resize an image in the browser before upload.
 * PNG and WebP keep their type so logos can stay transparent.
 * Other images are saved as JPEG.
 */
export const compressImageFile = (file, { maxEdge = 1200, quality = 0.82 } = {}) => {
    if (!file || typeof file.type !== "string" || !file.type.startsWith("image/")) {
        return Promise.resolve(file);
    }
    if (file.type === "image/gif" || file.type === "image/svg+xml") {
        return Promise.resolve(file);
    }

    const outputType = file.type === "image/png" || file.type === "image/webp"
        ? file.type
        : "image/jpeg";

    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            URL.revokeObjectURL(url);
            const longest = Math.max(img.width, img.height) || 1;
            const scale = Math.min(1, maxEdge / longest);
            const width = Math.max(1, Math.round(img.width * scale));
            const height = Math.max(1, Math.round(img.height * scale));
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext("2d");
            if (!context) {
                resolve(file);
                return;
            }
            context.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
                if (!blob || (blob.size >= file.size && scale === 1)) {
                    resolve(file);
                    return;
                }
                const extension = outputType === "image/png" ? ".png" : outputType === "image/webp" ? ".webp" : ".jpg";
                const name = file.name.replace(/\.[^.]+$/, "") + extension;
                resolve(new File([blob], name, { type: outputType, lastModified: Date.now() }));
            }, outputType, outputType === "image/png" ? undefined : quality);
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
        };

        img.src = url;
    });
};
